import { Text, useToast, Flex, Center, Box } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { SubmitHandler } from "react-hook-form";
import { useQueryClient } from "react-query";

import { ContactDto, CorporationDto } from "@bcasmart/doxecure-commons/doxecureClientTypes";
import { userName } from "@bcasmart/doxecure-commons/doxecureFunctions";
import { ContactType, UserProfile } from "@bcasmart/doxecure-commons/doxecureTypes";
import { $email, $ok } from "foundation-ts/commons";
import { UUID } from "foundation-ts/types";

import { ColumnTitle } from "@components/ColumnTitle/ColumnTitle";
import { ContactsList } from "@components/ContactsList";
import { ContactForm, NewPersonFormValues } from "@/components/ContactForm/ContactForm";
import { G1Button } from "@/components/G1Button";
import { ContactsImportTable } from "@/components/Import/ContactsImportTable";
import { SelectImportFileModal } from "@/components/Import/SelectImportFileModal";
import { ContactToImport } from "@/components/Import/types";
import { AsyncConfirmModal } from "@components/Modals/AsyncConfirmModal";
import { DefaultAsyncConfirmModalState } from "@components/Modals/ModalConstants";
import { PageShellWithThreeColumns } from "@components/PageShellWithThreeColumns/PageShellWithThreeColumns";
import { SeaShellBox } from "@components/SeaShellBox";
import { TagFilterBar } from "@components/Tags/TagFilterBar";
import { config } from "@/config/config";
import { ContactsQueryID } from "@/hooks/hooksConstants";
import { useAuthContext } from "@hooks/useAuthContext";
import { useContactImport } from "@/hooks/useImportContacts";
import { useDevice } from "@/hooks/useDevice";
import { getCorporationUsers } from "@/services/corporations.service";
import { addUserContact, removeContact, updateContact } from "@/services/users.service";
import { $g1Color } from "@/theme/G1Style";
import { parseContactsCsv } from "@utils/csvImport";
import { optlog, trueOrEmptyCard } from "@utils/functions";
import { G1Icon } from "@utils/icons";
import { defaultErrorToast, defaultInfoToast, defaultSuccessToast } from "@utils/toast";
import { BaseViewProps } from "@utils/TypesAndConstants";

export const ContactsListView = (props: BaseViewProps) => {
    const { lockedAccount } = props;
    const { connectedUser, refreshConnectedUser } = useAuthContext();
    const queryClient = useQueryClient();
    const { contacts, phase, reset, startImport, setContacts, uploadContactsList } = useContactImport(connectedUser?.apid, config.app.importContactConcurrency);
    const [modal, setModal] = useState(DefaultAsyncConfirmModalState);
    const [corporateUsersEmails, setCorporateUsersEmails] = useState<string[]>([]);
    const [allContacts, setAllContacts] = useState<ContactDto[]>([]);
    const [rightPaneContent, setRightPaneContent] = useState<RightPanelType | undefined>(undefined);
    const [selectedContact, setSelectedContact] = useState<ContactDto|undefined>(undefined);
    const [selectedIds, setSelectedIds] = useState<Set<UUID>>(new Set());
    const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
    const [importFilename, setImportFilename] = useState("");
    const [openSelectModal, setOpenSelectModal] = useState(false);
    const { isMOBILE } = useDevice() ;
    const blacklistemail = useMemo(() => {
        const emails = new Set([
            ...corporateUsersEmails,
            ...allContacts.map(c => c.contact.email),
        ]);
        return Array.from(emails);
    }, [corporateUsersEmails, allContacts]);
    const toast = useToast();

    const isCorporate = connectedUser?.userFlags?.profile === UserProfile.AdministratorCorporate || connectedUser?.userFlags?.profile === UserProfile.UserCorporate;
    const userTags = connectedUser?.tags ?? {};
    optlog(`+++ ContactsListView initialized with profile ID '${connectedUser?.apid}'`);

    const handleAllContactsLoaded = (contacts: ContactDto[]) => {
        setAllContacts(contacts);
    };

    const onViewContact = (contact: ContactDto) => {
        setRightPaneContent(RightPanelType.ContactUpdate);
        setSelectedContact(contact);
    };

    const onViewClose = () => {
        reset();
        setRightPaneContent(undefined);
        setSelectedContact(undefined);
    }

    const onToggleSelect = (item: ContactDto) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(item.apid!)) { next.delete(item.apid!); } else { next.add(item.apid!); }
            return next;
        });
    };

    const onToggleSelectAll = (checked: boolean, items: ContactDto[]) => {
        setSelectedIds(checked ? new Set(items.map(i => i.apid!)) : new Set());
    };

    const onAddContact: SubmitHandler<NewPersonFormValues> = async (values) => {
        try {
            const mail = $email(values.email.toLowerCase());
            if (!$ok(mail)) { throw 'Bad email'; }
            const newContact = await addUserContact(connectedUser!.apid!, { email: mail!, lastName: values.lastName.toUpperCase().trim(), firstName: values.firstName?.trim().capitalize(), phone: values.phone?.trim(), tags: values.tags });
            if (!$ok(newContact) || !$ok(newContact.apid)) { throw 'Bad contact creation'; }

            queryClient.invalidateQueries([ContactsQueryID, connectedUser!.apid!]);
            await refreshConnectedUser();
            toast(defaultSuccessToast(`${userName(newContact.contact)} ajouté.`));
        } catch(error) {
            toast(defaultErrorToast("Les informations renseignées ne sont pas valides."));
        }
    };

    const onUpdateContact: SubmitHandler<NewPersonFormValues> = async (values) => {
        try {
            const mail = $email(values.email.toLowerCase());
            if (!$ok(mail)) { throw 'Bad email'; }

            const phone = !$ok(values.phone) && (selectedContact?.contact.phone) ? null : values.phone?.trim();
            const updatedContact = await updateContact(connectedUser!.apid!, selectedContact!.apid!, { email: mail!, lastName: values.lastName.toUpperCase().trim(), firstName: values.firstName.trim().capitalize(), phone: phone, tags: values.tags });
            queryClient.invalidateQueries([ContactsQueryID, connectedUser!.apid!]);
            await refreshConnectedUser();
            setRightPaneContent(undefined);
            toast(defaultSuccessToast(`${userName(updatedContact.contact)} a été mis à jour.`));
        } catch (error) {
            toast(defaultErrorToast("Impossible de modifier les informations du contact"));
        }
    };

    const onRemoveContact = async (contact?: ContactDto) => {
       if (!!lockedAccount?.exceedsUsersLimit || !!lockedAccount?.canceledAndWaitingSessionEnds) { return; } // Nothing to do when account is blocked
       if (!$ok(contact)) { return; }

        const contactName = `${contact!.contact.firstName?.capitalize()} ${contact!.contact.lastName.toUpperCase()}`;
        setModal({
            message: `Êtes-vous sûr de vouloir supprimer ${contactName} de votre liste de contacts ?`,
            onClose: async () => setModal(DefaultAsyncConfirmModalState),
            onAccept: async () => {
                try {
                    const removed = await removeContact(connectedUser!.apid!, contact!.apid!);
                    if (removed) {
                        queryClient.invalidateQueries([ContactsQueryID, connectedUser!.apid!, ContactType.All]);
                        await refreshConnectedUser();
                        toast(defaultSuccessToast(`${contactName} ne fait plus partie de votre liste de contacts`));
                    } else {
                        toast(defaultErrorToast(`Impossible de supprimer ${contactName} de votre liste de contacts`));
                    }
                } catch (e) {
                    toast(defaultErrorToast((e as Error).message));
                }
                setModal(DefaultAsyncConfirmModalState);
            },
        });
    };

    const onRemoveSelectedContacts = async () => {
        if (!!lockedAccount?.exceedsUsersLimit || !!lockedAccount?.canceledAndWaitingSessionEnds) { return; } // Nothing to do when account is blocked
        if (selectedIds.size === 0) { return; }

        const count = selectedIds.size;
        setModal({
            message: `Êtes-vous sûr de vouloir supprimer ${count === 1 ? 'ce contact' : `ces ${count} contacts`} de votre liste ?`,
            onClose: async () => setModal(DefaultAsyncConfirmModalState),
            onAccept: async () => {
                const ids = Array.from(selectedIds);
                const results = await Promise.allSettled(ids.map(id => removeContact(connectedUser!.apid!, id)));
                const failedCount = results.filter(r => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value)).length;

                queryClient.invalidateQueries([ContactsQueryID, connectedUser!.apid!, ContactType.All]);
                await refreshConnectedUser();
                setSelectedIds(new Set());
                setModal(DefaultAsyncConfirmModalState);
                if (failedCount === 0) {
                    toast(defaultSuccessToast(`${ids.length} contact${ids.length > 1 ? 's supprimés' : ' supprimé'} de votre liste de contacts`));
                } else {
                    toast(defaultErrorToast(`${failedCount} contact(s) n'ont pas pu être supprimés`));
                }
            },
        });
    };
    
    const onImportContacts = async (toImport: ContactToImport[]) => {
        if (toImport.length <= config.app.maxImportInteractiveContacts) {
            await startImport(contacts, toImport);
            queryClient.invalidateQueries([ContactsQueryID, connectedUser!.apid!]);
        } else {
            const ret = await uploadContactsList(toImport, config.timeouts.contactsImport);
            if (ret === 'failed') {
                toast(defaultErrorToast("Une erreur a été détectée lors de l'envoi de la liste des contacts"));
            } else {
                const msg = ret === "waiting" ? "Votre liste de contacts est en cours de traitement, vous recevrez une notification par mail lorsque celle-ci sera totalement traitée" : "Votre liste de contacts a été importée avec succès";
                toast(defaultSuccessToast(msg));
            }
        }
    };

    const onParseSelectedFile = async (file: File) => {
        try {
            const contacts = await parseContactsCsv(file, allContacts, corporateUsersEmails);
            if (contacts.length > 0) {
                reset();
                setImportFilename(file.name);
                setContacts(contacts);
                setRightPaneContent(RightPanelType.ContactImport);
                setOpenSelectModal(false);
            }
        } catch (e) {
            toast(defaultErrorToast((e as Error).message));
        }
    };

    useEffect(() => {
        setSelectedIds(prev => {
            if (prev.size === 0) return prev;
            const validIds = new Set(allContacts.map(c => c.apid));
            const next = new Set(Array.from(prev).filter(id => validIds.has(id)));
            return next.size === prev.size ? prev : next;
        });
    }, [allContacts]);

    useEffect(() => {
        const setCorporationUsersList = async (connectedUserId: UUID, corporations: CorporationDto[]) => {
            const users: string[] = [] ;
            for (let corporation of corporations) {
                const usersDto = await getCorporationUsers(corporation.apid) ;
                if ($ok(usersDto)) {
                    const filteredUsers = usersDto!.filter(userDto => (userDto.apid !== connectedUserId));
                    users.push(...filteredUsers.map(userDto =>  userDto.email));
                }
            }
            setCorporateUsersEmails(users) ;
        }

        if (isCorporate) {
            setCorporationUsersList(connectedUser!.apid!, connectedUser!.corporations!);
        } else {
            setCorporateUsersEmails([]) ;
        }
    }, [isCorporate]);

    return trueOrEmptyCard(
        $ok(connectedUser),
        <PageShellWithThreeColumns
            centralContent={
                <Flex direction="column" height="100%" overflow="hidden">
                    <Box
                        position="sticky"
                        top="0"
                        zIndex="2"
                        pb={3}
                    >
                        <Flex direction={{ base: "column", sm: "row" }} justify="space-between" align={{ base: "stretch", sm: "center" }} gap={3} wrap="wrap">
                            <ColumnTitle color={$g1Color('global.write')}>
                                <Text>Contacts</Text>
                            </ColumnTitle>
                            <Flex wrap="wrap" gap={2} justify={{ base: "flex-start", sm: "flex-end" }} pt={{ base: 0, sm: "5" }}>
                                {selectedIds.size > 0 && (
                                    <G1Button component="form" variant="important" title={`Supprimer ${selectedIds.size} contact${selectedIds.size > 1 ? 's' : ''}`} fontSize="sm" leftIcon={<G1Icon.Minus />} disabled={phase === "importing"} onClick={onRemoveSelectedContacts} />
                                )}
                                <G1Button component="form" variant="ok" title="Ajouter un contact" fontSize="sm" leftIcon={<G1Icon.Plus />} disabled={phase === "importing"} onClick={() => setRightPaneContent(RightPanelType.ContactCreation)} />
                                <G1Button component="form" variant="cancel" title="Importer des contacts" fontSize="sm" leftIcon={<G1Icon.Upload />} disabled={phase === "importing"} onClick={() => { setRightPaneContent(undefined); setOpenSelectModal(true); }} />
                            </Flex>
                        </Flex>
                    </Box>

                    {Object.keys(userTags).length > 0 && (
                        <SeaShellBox component="form" mb={3}>
                            <Text mb={1}>Filtrer par catégorie</Text>
                            <TagFilterBar
                                tags={userTags}
                                selected={selectedTags}
                                onToggle={(key) => setSelectedTags(prev => {
                                    const next = new Set(prev);
                                    next.has(key) ? next.delete(key) : next.add(key);
                                    return next;
                                })}
                            />
                        </SeaShellBox>
                    )}

                    <Box flex="1" overflowY="auto">
                        <ContactsList connectedUserProfile={connectedUser!} ordered displayIcon selectable lockedAccount={lockedAccount}
                                      selectedIds={selectedIds} tagFilters={selectedTags}
                                      onContactsLoaded={handleAllContactsLoaded}
                                      onToggleSelect={onToggleSelect}
                                      onToggleSelectAll={onToggleSelectAll}
                                      onRemoveItemClick={onRemoveContact} 
                                      onViewItemClick={phase === "importing" ? () => toast(defaultInfoToast("Une importation de contacts est en cours, aucun contact existant ne peut être visualisé")) : onViewContact}
                            />
                    </Box>
                    <AsyncConfirmModal message={modal.message} onAccept={modal.onAccept} onClose={modal.onClose} />
                    <SelectImportFileModal isOpen={openSelectModal} onClose={() => setOpenSelectModal(false)} onParseSelectedFile={onParseSelectedFile} />
                </Flex>
            }
            rightContent={
                <>
                    {isMOBILE && $ok(rightPaneContent) && (
                        <ColumnTitle color={$g1Color('global.write')}>
                            <Flex alignItems="center" as="button" onClick={onViewClose} width="100%">
                                <Center color={$g1Color('global.icons.navigation.previous.write')}><G1Icon.Back /></Center>
                                <Text>Retour</Text>
                            </Flex>
                        </ColumnTitle>
                    )}
                    {rightPaneContent === RightPanelType.ContactCreation && (
                        <ContactForm mode="create" connectedUserProfile={connectedUser} titleBtn="Contact" blacklist={blacklistemail} lockedAccount={lockedAccount} onSubmit={onAddContact}  /> 
                    )}
                    {rightPaneContent === RightPanelType.ContactUpdate && (
                        <ContactForm key={selectedContact?.apid} mode="edit" connectedUserProfile={connectedUser} initialValues={selectedContact} lockedAccount={lockedAccount} onSubmit={onUpdateContact} onDelete={onRemoveContact}  /> 
                    )}
                    {rightPaneContent === RightPanelType.ContactImport && (
                        <ContactsImportTable filename={importFilename} contacts={contacts} phase={phase} onClose={onViewClose} onConfirmImport={onImportContacts}  />
                    )}
                </>
            }
            isOpen={rightPaneContent !== undefined}
        />,
        false,
        !$ok(connectedUser),
        "Impossible de récupérer le profil de l'utilisateur",
        "Connexion invalide",
        "Chargement du profil de l'utilisateur"
    );
};

enum RightPanelType {
    ContactCreation,
    ContactUpdate,
    ContactImport,
}
